import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline, BookButton } from "@/components/bits";
import { THERAPISTS } from "@/lib/site";

export default function OmOss() {
  return (
    <>
      <Seo
        title="Om oss | Actus Osteopati – Osteopat i Oslo sentrum"
        description="Bli kjent med Actus Osteopati, en osteopatiklinikk på Lilletorget 1 i Oslo sentrum, og våre behandlere."
      />
      <PageHeader
        overline="Om klinikken"
        title="Om oss"
        testId="om-oss-heading"
      />

      <section data-testid="om-oss-therapists" className="bg-paper py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center">
            <Overline>Behandlere</Overline>
            <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight">
              Våre behandlere
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {THERAPISTS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <article data-testid={`om-oss-therapist-${t.name.split(" ")[0].toLowerCase()}`} className="group">
                  <div className="aspect-[3/4] overflow-hidden bg-cream">
                    <img
                      src={t.photo}
                      alt={`${t.name}, osteopat ved Actus Osteopati i Oslo sentrum`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-6 font-display font-normal text-2xl">{t.name}</h3>
                  <p className="mt-1 text-xs tracking-[0.25em] uppercase text-ink-soft/70">{t.title}</p>
                  {t.bio ? (
                    <div className="mt-4">
                      {t.bio.map((p) => (
                        <p key={p.slice(0, 24)} className="mt-3 first:mt-0 text-sm text-ink-soft leading-relaxed">{p}</p>
                      ))}
                      <p className="mt-7 text-xs tracking-[0.25em] uppercase text-ink-soft/70">Interesseområder</p>
                      <ul className="mt-3 space-y-1.5">
                        {t.interests.map((item) => (
                          <li key={item} className="text-sm text-ink-soft leading-relaxed">{item}</li>
                        ))}
                      </ul>
                      <p className="mt-7 text-xs tracking-[0.25em] uppercase text-ink-soft/70">Utdanning</p>
                      <ul className="mt-3 space-y-1.5">
                        {t.education.map((item) => (
                          <li key={item} className="text-sm text-ink-soft leading-relaxed">{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-ink-soft/70 italic">
                      Utdanning, erfaring og interesseområder legges inn.
                    </p>
                  )}
                  <a
                    data-testid={`om-oss-book-${t.name.split(" ")[0].toLowerCase()}`}
                    href="https://system.physica.no/book/actusosteopati"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink hover:text-sage-deep transition-colors duration-300"
                  >
                    Book time <ArrowRight size={14} />
                  </a>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="om-oss-cta" className="bg-beige text-ink py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight">
              Velkommen til en time hos oss
            </h2>
            <div className="mt-10 flex flex-wrap justify-center items-center gap-8">
              <BookButton testId="om-oss-book-button" dark label="Book time nå" />
              <Link
                data-testid="om-oss-contact-link"
                to="/kontakt"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink/70 hover:text-ink transition-colors duration-300"
              >
                Kontakt oss <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
